import { Formik, FormikHelpers } from 'formik';
import { useMemo, useState } from 'react';
import * as Yup from 'yup';

import { Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useQueryClient } from '@tanstack/react-query';
import { ContactName, ContactType } from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { useUserContactsQuery } from '@uplift/queries';
import {
  Button, LoadableTypography, MaskedInput, useInitiateContactVerification, useUpdateMobileNumber
} from '@uplift/ui-v2';
import { getFormErrorsFromRewriteUserContactByNameResponse } from '@uplift/utils';
import { VALIDATION_MOBILE_PHONE } from '@uplift/validations';

import {
  StepContainer, StepContent, StepDescription, StepForm, StepHeading, StepNavigator, StepNextButton
} from '../../components';
import { VerifyPhoneNextData } from '../types';

export type VerifyPhoneProps = {
  onBack: () => void;
  onNext: (data: VerifyPhoneNextData) => void;
};

type VerifyPhoneFormType = {
  phone: string;
};

export const VerifyPhone: React.VFC<VerifyPhoneProps> = props => {
  const currentUser = useCurrentuser();
  const { data: contacts = [] } = useUserContactsQuery(currentUser.id);
  const mobile = contacts.find(c => c.type === ContactType.phone && c.name === ContactName.mobile);
  const queryClient = useQueryClient();
  const { classes } = useVerifyPhoneStyles();
  const [ isEdit, setIsEdit ] = useState(false);
  const { initiateContactVerification, initiateContactVerificationLoading } = useInitiateContactVerification();
  const { updateMobileNumber } = useUpdateMobileNumber();

  const initialValues: VerifyPhoneFormType = {
    phone: mobile?.value || '',
  };

  const onSubmit = async (values: VerifyPhoneFormType, formikHelpers: FormikHelpers<VerifyPhoneFormType>) => {
    let contactId = mobile?.id;

    if (values.phone !== initialValues.phone) {
      try {
        const { data: mobileContact } = await updateMobileNumber({ name: ContactName.mobile, number: values.phone, userId: currentUser.id });

        contactId = mobileContact.id;
        await queryClient.invalidateQueries([ 'user', currentUser.id, 'contacts' ]);
      } catch (error) {
        const errorObj = getFormErrorsFromRewriteUserContactByNameResponse(error, ContactName.mobile);

        if (errorObj && errorObj[ContactName.mobile]) {
          formikHelpers.setErrors({ phone: errorObj[ContactName.mobile] });
          return;
        }

        console.error('Error updating contacts', error);
        return;
      }
    }

    await initiateContactVerification({ contactId });
    props.onNext({ contactVerificationInitiated: true });
  };

  const phoneDisplay = useMemo(() => {
    if (isEdit) {
      return (
        <div className={classes.phoneForm}>
          <MaskedInput
            label="Mobile"
            autoFocus
            name="phone"
            type="phone"
            inputClasses={{ root: classes.phoneInput }}
            color="secondary"
          />
        </div>
      );
    }

    return (
      <div className={classes.phoneContainer}>
        <LoadableTypography component="b" fontWeight={500} width="120px" data-testid="mobile-phone-number">{mobile?.value}</LoadableTypography>
        <Button color="secondary" size="small" className={classes.button} onClick={() => setIsEdit(true)} data-testid="edit-phone-btn">Edit</Button>
      </div>
    );
  }, [ classes, isEdit, mobile?.value ]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
      validateOnMount
    >
      {formik => (
        <StepContainer>
          <StepContent>
            <StepHeading>Stay up to date with notifications</StepHeading>
            <StepDescription className={classes.description}>Verify your mobile number to receive reminders via text for appointments and important information about your account.</StepDescription>
            <StepForm className={classes.form} onSubmit={formik.handleSubmit} maxWidth="384px">
              <Typography>A verification code will be sent to:</Typography>
              {phoneDisplay}
            </StepForm>
          </StepContent>
          <StepNavigator onBack={props.onBack}>
            <StepNextButton disabled={!formik.isValid || initiateContactVerificationLoading} loading={initiateContactVerificationLoading} data-testid="send-code-btn">send verification code</StepNextButton>
            <Button color="secondary" onClick={() => props.onNext({ contactVerificationInitiated: false })} data-testid="skip-for-now-btn">skip for now</Button>
          </StepNavigator>
        </StepContainer>
      )}
    </Formik>
  );
};

const validationSchema = (): Yup.ObjectSchema<VerifyPhoneFormType> => Yup.object().shape({
  phone: VALIDATION_MOBILE_PHONE,
});

const useVerifyPhoneStyles = makeStyles({ name: 'VerifyPhone' })(theme => ({
  description: {
    marginTop: theme.spacing(3)
  },
  form: {
    marginTop: theme.spacing(2),
  },
  phoneContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    marginTop: theme.spacing(0.5)
  },
  phoneForm: {
    marginTop: theme.spacing(2)
  },
  phoneInput: {
    maxWidth: '220px'
  },
  button: {
    textTransform: 'none'
  }
}));
